import React, { Fragment, useState, useLayoutEffect, useEffect, useCallback } from 'react';
import { useMediaQuery, Hidden, NoSsr, Box } from '@material-ui/core';
import { withNamespaces } from 'react-i18next';
import { withSitecoreContext, Text } from '@sitecore-jss/sitecore-jss-react';
import isEmpty from 'lodash/isEmpty';
import clsx from 'clsx';
import get from 'lodash/get';
import debounce from 'lodash/debounce';
import withErrorBoundary from '../../../utils/HOCs/withErrorBoundary';
import Grid from '../../../components/molecules/Grid';
import useStyles from './style';
import Overlay from '../../../components/atoms/Overlay';
import HeroCarousel from '../../../components/organism/HeroCarousel';
import Button from '../../../components/atoms/Button';
import PrefetchImages from '../../../utils/PrefetchImages';
import { breakpoints } from '../../../../styles/themes/vad/constants';
import Typography from '../../../components/atoms/Typography';
import DctSvgIcon from '../../../components/atoms/Svg';
import { COMPONENT_NAME } from './locators';
import { pageContentType } from '../../../utils/getContentType';
import { getDictionaryText } from '../../../utils/getDictionaryText';
import { canUseDOM } from '../../../utils/canUseDOM';
import {
  getPageName,
  getComponentName,
} from '../../../utils/getPageComponentInfo';
import DisplayImageOrVideo from '../../../components/organism/DisplayImageOrVideo';
import { VerticalFirst, VerticalSecond, VerticalThird } from './constants';

const ResponsiveCollage = props => {
  const [deskstopCollageData, setDeskstopCollageData] = useState({})
  const [mobileCollageData, setMobileCollageData] = useState({})

  const title = get(props, 'fields.title', { value: '' })
  const variation = get(props, 'params.variation', VerticalFirst);

  const galleryHasVideo = useCallback((items) => {
    const updateItems = items.map(x => {
      const video = x.fields?.video?.value || null
      if (!isEmpty(video) && !video.href) {
        return { ...x, ['fields']: { ...x['fields'], video: { ...x['fields']['videoUrl'], value: { href: video, url: video, linktype: 'external' } } } }
      }
      return x
    })
    return updateItems
  }, [gallery])

  const gallery = Array.isArray(props.fields.items) && galleryHasVideo(props.fields.items);
  const items = gallery || []

  useEffect(() => {
    let desktopCollageData = null
    let mobileCollageData = null

    if (items.length === 5) {
      const collageLayouts = {
        [VerticalFirst]: [[items[0]], [items[1], items[2]], [items[3], items[4]]],
        [VerticalSecond]: [[items[0], items[1]], [items[2]], [items[3], items[4]]],
        [VerticalThird]: [[items[0], items[1]], [items[2], items[3]], [items[4]]],
      }

      desktopCollageData = collageLayouts[variation]
      mobileCollageData = [[items[0]], [items[1], items[2]], [items[3]]]
    }
    setDeskstopCollageData(desktopCollageData)
    setMobileCollageData(mobileCollageData)
  }, [props])

  const createImageOrVideo = (data) => {
    return (
      <DisplayImageOrVideo mediaField={data} isParalax={false} from='videoHeight' playVideo={playVideo} />
    )
  }

  const getId = (event) => {
    const id = event.currentTarget.getAttribute('data-id');
    setOverlayOpen('', id)
  }

  const DisplayDoubleImage = ({ data }) => {

    return (
      <Box className={'boxA'}>
        <Box className={'boxAA'} data-id={data[0].id} onClick={getId}>
          {createImageOrVideo(data[0].fields)}
        </Box>
        <Box className={'boxAA'} data-id={data[1].id} onClick={getId}>
          {createImageOrVideo(data[1].fields)}
        </Box>
      </Box>
    )
  }

  const DisplaySingleImage = ({ data }) => {
    return (
      <Box className={'boxB'} data-id={data[0].id} onClick={getId}>
        {createImageOrVideo(data[0].fields)}
      </Box>
    )
  }


  const DisplayMobileDoubleImage = ({ data }) => {

    return (
      <Box className={'boxMobA'}>
        <Box className={'boxMobAA'} data-id={data[0].id} onClick={getId}>
          {createImageOrVideo(data[0].fields)}
        </Box>
        <Box className={'boxMobAA'} data-id={data[1].id} onClick={getId}>
          {createImageOrVideo(data[1].fields)}
        </Box>
      </Box>
    )
  }

  const DisplayMobileSingleImage = ({ data }) => {
    return (
      <Box className={'boxMobB'} data-id={data[0].id} onClick={getId}>
        {createImageOrVideo(data[0].fields)}
      </Box>
    )
  }

  const CreateCollage = useCallback(({ video, id = 1, image }) => {
    return (
      <div className={clsx(
        'collage-video-icon',
        image && image.value && !image.value.src && 'fallBackColor',
        isMobile ? classes.ResponsiveMobileCollageWrapper : classes.ResponsiveCollageWrapper
      )}
      >
        {
          !isMobile && Array.isArray(deskstopCollageData) ? deskstopCollageData.map((element, i) => {
            if (element.length === 2) {
              return (
                <>
                  <DisplayDoubleImage key={i} data={element} />
                </>
              )
            }
            else if (element.length === 1) {
              return <DisplaySingleImage key={i} data={element} />
            }
            return <></>
          })
            : (
              <>
                {
                  Array.isArray(mobileCollageData) && mobileCollageData.map((element, i) => {
                    if (element.length === 2) {
                      return <DisplayMobileDoubleImage key={i} data={element} />
                    }
                    else if (element.length === 1) {
                      return <DisplayMobileSingleImage key={i} data={element} />
                    }
                    else {
                      return <></>
                    }
                  })
                }
              </>
            )
        }
        <Button
          component="span"
          buttonType="primary"
          hasBorder={false}
          buttonIcon={buttonIcon}
          srText={getDictionaryText('seeAll')}
          className={`${classes.seeAllBtn} seeAllBtn`}
          onClick={() => setIsVisible(true)}
          animateSettings={{
            willAnimate: false,
          }}
        >
          <Typography variant="body3">
            {/* <Text field={{ value: getDictionaryText('seeAll') }} /> */}
            <Text field={{ value: 'See all photos' }} />
          </Typography>
        </Button>
      </div>
    )
  }, [deskstopCollageData, mobileCollageData])


  const pageName = getPageName(props);
  const componentName = getComponentName(props);
  const contentType = pageContentType(props).toLowerCase();
  const [renditionNeeded, setRenditionNeeded] = useState(true);

  if (gallery && gallery.length > 0) {
    if (
      renditionNeeded &&
      !gallery.some(
        item =>
          get(item, 'fields.image.value[16x9-m]') ||
          get(item, 'fields.image.value[16x6]')
      )
    ) {
      setRenditionNeeded(false);
    }
  }

  const classes = useStyles({ contentType });
  // const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(`(max-width:${1023}px)`);
  const [isOverlayVisible, setVisibleState] = useState(false);
  const [handleOpenState, setOpenState] = useState(false);
  const [handleBtnClickHandler, setHandleBtnClickHandler] = useState(
    false
  );
  const [currentSlidePos, setCurrentSlidePos] = useState(1);
  const [buttonIconName, setButtonIconName] = useState('ImageOutlinedIcon');

  useLayoutEffect(() => {
    if (canUseDOM) {
      function setWrapperHeight() {
        let el = document.querySelector('.mediaWrapper');
        if (el) {
          const availWidth = window.innerWidth;
          let minHeight = 0;
          if (availWidth >= breakpoints.values.lg) {
            minHeight = (availWidth * 6) / 16;
          } else {
            minHeight = (availWidth * 9) / 16;
          }
          el.style.minHeight = minHeight - 20 + 'px';
          setTimeout(() => {
            if (el.querySelector('.noRendition')) {
              el.querySelector('.noRendition').style.height = minHeight + 'px';
            }
          }, 0);
        }
      }
      const setHeightWithDebounce = debounce(setWrapperHeight, 500);
      setWrapperHeight();
      window.addEventListener('resize', setHeightWithDebounce);
      return () => window.removeEventListener('resize', setHeightWithDebounce);
    }
  }, []);

  const setIsVisible = isVisible => {
    setVisibleState(isVisible);
    setHandleBtnClickHandler(!isVisible);
    if (!isVisible) {
      // resetting slide count to 1 after closing
      // the overlay
      setCurrentSlidePos(1);
    }
  };

  // const addAnalyticsInfo = (isCollageClick = false, video) => {
  //   let pushData;
  //   if (isCollageClick) {
  //     pushData =
  //       get(video, 'value.href', '') && get(video, 'value.href', '').length > 0
  //         ? get(analyticsData, 'pageBodyInteraction.galleryVideoClick')
  //         : get(analyticsData, 'pageBodyInteraction.galleryImageClick');
  //   } else {
  //     pushData = get(analyticsData, 'pageBodyInteraction.seeAllPhotosBtnClick');
  //   }
  //   const obj = {
  //     ...pushData,
  //     ...{
  //       label: createDynamicLabels([
  //         pageName,
  //         componentName,
  //         title && title.value,
  //       ]),
  //     },
  //   };
  //   pushAnaylyticsData(obj);
  // };

  const setOverlayOpen = (video, id) => {
    // addAnalyticsInfo(true, video);
    setVisibleState(true);
    if (isMobile) {
      setHandleBtnClickHandler(true);
    }
    if (Array.prototype.findIndex) {
      setCurrentSlidePos(gallery.findIndex(item => item.id === id) + 1);
    } else {
      const galleryWithIds = gallery.map(item => item.id);
      setCurrentSlidePos(galleryWithIds.indexOf(id) + 1);
    }
  };

  const playVideo = video => {
    const videoHref = get(video, 'href', '') || '';
    const hasVideo = !isEmpty(videoHref);
    setOpenState(hasVideo);
    if (hasVideo && canUseDOM) {
      setHandleBtnClickHandler(true);
      let playBtn = document.querySelector(
        '.responsive-hero-carousel .slick-active .plyr__control--overlaid'
      );
      let playBtnYouku = document.querySelector('.slick-active .youkuPlayIcon');
      const playBtnInterval = setInterval(() => {
        playBtn = document.querySelector(
          '.responsive-hero-carousel .slick-active .plyr__control--overlaid'
        );
        playBtnYouku = document.querySelector('.responsive-hero-carousel .slick-active .youkuPlayIcon');
        if (playBtn || playBtnYouku) {
          clearInterval(playBtnInterval);
          setTimeout(() => {
            setOpenState(false);
            playBtn && playBtn.click();
            playBtnYouku && playBtnYouku.click();
          }, 100);
        }
      }, 100);
    }
  };

  if (gallery && gallery.length === 0) {
    return <></>
  }

  const renderTile = ({
    item: { image, mobileImage, video, youkuVideo },
    ratio = '16x9-m',
    id,
  }) => {
    // const videoUrl = isInsideChina(props && props.sitecoreContext)
    //   ? youkuVideo
    //   : video;
    return (
      <CreateCollage key={id} video={video} id={id} image={image} />
    );
  };

  const renderTiles = () => {
    let featuredGallery =
      gallery &&
      gallery.length > 0 &&
      gallery.filter(
        ({ fields }) =>
          fields.isFeatured &&
          (fields.isFeatured.value === 'true' || // handling like this because sometimes sitecore sends it in string also
            fields.isFeatured.value === true)
      );
    // set first gallery as featured
    // when author forgot to add any featured images
    if (isEmpty(featuredGallery)) {
      featuredGallery = [gallery[0]];
    }

    // Updating see all button icon on the basis of first featured
    // banner is video or image
    if (
      (get(featuredGallery[0], 'fields.video.value.href') || '').length > 0 &&
      buttonIconName !== 'CameraAltIcon'
    ) {
      setButtonIconName('CameraAltIcon');
    }

    // we have to show first featured gallery
    // when we are rendering mobile version or we have
    // less than 3 featured image
    if (
      isMobile ||
      (featuredGallery &&
        featuredGallery.length > 0 &&
        featuredGallery.length < 3)
    ) {
      return (
        <Grid item xs={12} className="singleFeatureImage">
          {renderTile({
            item: featuredGallery[0].fields,
            ratio: '16x6',
            id: featuredGallery[0].id,
          })}
        </Grid>
      );
    }

    const renderOnlyThreeImage =
      featuredGallery.length >= 3 && featuredGallery.slice(0, 3);
    const [firstTile, ...restTiles] = renderOnlyThreeImage;
    return (
      <Fragment>
        <Grid item xs={12} md={8}>
          {firstTile &&
            renderTile({ item: firstTile.fields, id: firstTile.id })}
        </Grid>
        <Grid item xs={12} md={4}>
          <Grid container>
            {restTiles &&
              restTiles.map((tile, index) => (
                <Grid item className="grid-item" key={index}>
                  <Hidden mdDown implementation="css">
                    {renderTile({ item: tile.fields, id: tile.id })}
                  </Hidden>
                </Grid>
              ))}
          </Grid>
        </Grid>
      </Fragment>
    );
  };

  const getCurrentSlidePos = pos => {
    setCurrentSlidePos(pos);
  };
  const titleNode = (
    <Fragment>
      <Text field={title} />
      <Typography className="slideCount" variant="labelRegular">
        {`${currentSlidePos} / ${gallery.length}`}
      </Typography>
    </Fragment>
  );

  const buttonIcon = <DctSvgIcon name={buttonIconName} />;

  const prefetchImagesMobile = [];
  const prefetchImagesDesktop = [];

  gallery &&
    gallery.length > 0 &&
    gallery.forEach(image => {
      prefetchImagesMobile.push(
        get(image, 'fields.mobileImage.value[3x2]', '')
      );
    });

  gallery &&
    gallery.length > 0 &&
    gallery.forEach(image => {
      prefetchImagesDesktop.push(get(image, 'fields.image.value[3x2]', ''));
    });
  return (
    <>
      {!isEmpty(gallery) && (
        <div className="mediaWrapper">
          <PrefetchImages
            desktopImageArray={prefetchImagesDesktop}
            mobileImageArray={prefetchImagesMobile}
          />
          <div
            className={clsx(classes.root, { singleGallery: gallery.length === 1 })}
            data-locator={COMPONENT_NAME}
          >
            <Grid container>
              {renderTiles()}
              <Overlay
                title={titleNode}
                withButton={false}
                // addAnalyticsInfo={addAnalyticsInfo}
                className={clsx(
                  'responsive-hero-carousel',
                  classes.overlayWrapper,
                  'overlay-wrapper',
                  { hidden: handleOpenState },
                  { 'video-playing': handleOpenState }
                )}
                isVisible={isOverlayVisible}
                setIsVisible={isVisible => setIsVisible(isVisible)}
                enableClickAway={true}
              >
                <HeroCarousel
                  titleText={(title && title.value) || ''}
                  carouselItems={gallery}
                  hasThumbnail
                  isMobile={isMobile}
                  thumbnailNeeded={false}
                  aspectRatio="3x2"
                  componentName={componentName}
                  pageName={pageName}
                  getCurrentSlidePos={pos => getCurrentSlidePos(pos)}
                  handleOpenState={handleOpenState}
                  shouldShowThumb={handleBtnClickHandler}
                  carouselConfig={{
                    initialSlide: currentSlidePos - 1,
                    fade: true,
                    swipe: true,
                    lazyLoad: false,
                    responsive: [
                      {
                        breakpoint: 1023,
                        settings: {
                          fade: false,
                          swipe: true,
                        },
                      },
                    ],
                  }}
                  isParalax={false}
                />
              </Overlay>
            </Grid>
          </div>
        </div>
      )}</>
  );
};

export default withErrorBoundary(
  withSitecoreContext()(withNamespaces()(ResponsiveCollage))
);
